import React, {
  useState,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
// @dynamic-i18n-keys
// galleryDisplaySettings.optimized.*
// galleryDisplaySettings.selected.*
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import DSModal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import RadioButton from '@wix/da-ds/pkg/RadioButton';
import MultiselectField from '@wix/da-ds/pkg/Dropdown/multiselect/MultiselectField';
import { DropdownOption } from '@wix/da-ds/pkg/Dropdown/types';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import CoreSymbol from '@wix/da-ds/pkg/Indicators/CoreSymbol';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { PapiGallection } from '@wix/da-papi-types';
import { ALL_FOLDER_ID, SCRAPS_FOLDER_ID } from '@wix/da-url';
import BuyCoreBrandedButton from '@wix/da-shared-react/pkg/Button/core/dsPresets/BuyCoreBrandedButton';
import { BiData, BuyCoreButtonBiEvent } from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { GallerySettings, GalleryOrder } from '../../types/gallerySettings';
import { buildGalleryHierarchy, getFeaturedFolderId } from './helpers';
import s from './GalleryDisplaySettingsModal.scss';

export enum GalleryDisplayType {
  OPTIMIZED = 'optimized',
  SELECTED = 'selected',
}

const DISPLAY_TYPE_OPTIONS = [
  {
    type: GalleryDisplayType.OPTIMIZED,
    titleKey: 'galleryDisplaySettings.optimized.title',
    descriptionKey: 'galleryDisplaySettings.optimized.description',
    descriptionId: 'optimized-description',
  },
  {
    type: GalleryDisplayType.SELECTED,
    titleKey: 'galleryDisplaySettings.selected.title',
    descriptionKey: 'galleryDisplaySettings.selected.description',
    descriptionId: 'selected-description',
  },
] as const;

export interface Props {
  requestClose?: () => void;
  settings: GallerySettings | null;
  isLoading: boolean;
  galleries: PapiGallection[];
  onLoadSettings: () => void;
  onSaveSettings: (settings: GallerySettings) => void;
  onResetState: () => void;
}

const GalleryDisplaySettingsModal: React.FC<Props> = ({
  requestClose,
  settings,
  isLoading,
  galleries,
  onLoadSettings,
  onSaveSettings,
  onResetState,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { hasCoreMembership } = useContext(SessionContext);

  // Determine Featured folder ID: find by name or use first folder
  const featuredFolderId = useMemo(
    () => getFeaturedFolderId(galleries),
    [galleries]
  );

  const [selectedDisplayType, setSelectedDisplayType] =
    useState<GalleryDisplayType>(GalleryDisplayType.OPTIMIZED);
  const [selectedGallery, setSelectedGallery] = useState<string>('');

  // Load settings when modal mounts, reset when modal unmounts
  useEffect(() => {
    // Always load settings on mount to trigger gallery loading
    // (even if settings exist, galleries may have been cleared by previous reset)
    onLoadSettings();

    return () => {
      onResetState();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update local state when settings are loaded
  useEffect(() => {
    if (settings) {
      // Determine display type based on BOTH folderId and order
      const displayType =
        settings.folderId === ALL_FOLDER_ID &&
        settings.order === GalleryOrder.PERSONALIZED
          ? GalleryDisplayType.OPTIMIZED
          : GalleryDisplayType.SELECTED;

      setSelectedDisplayType(displayType);
      setSelectedGallery(settings.folderId.toString());
    } else {
      // Reset to defaults when settings are cleared
      setSelectedDisplayType(GalleryDisplayType.OPTIMIZED);
      setSelectedGallery('');
    }
  }, [settings]);

  const handleDisplayTypeChange = useCallback(
    (type: GalleryDisplayType) => {
      setSelectedDisplayType(type);

      // When switching to SELECTED mode, set to All if no gallery selected
      if (type === GalleryDisplayType.SELECTED && !selectedGallery) {
        setSelectedGallery(ALL_FOLDER_ID.toString());
      }
    },
    [selectedGallery]
  );

  const handleSave = useCallback(() => {
    const isOptimized = selectedDisplayType === GalleryDisplayType.OPTIMIZED;

    // Validate parsed gallery ID to prevent NaN being sent to backend
    const folderIdNum = parseInt(selectedGallery, 10);
    const finalFolderId = !isNaN(folderIdNum) ? folderIdNum : ALL_FOLDER_ID;

    const updatedSettings: GallerySettings = isOptimized
      ? {
          // OPTIMIZED mode: ALL_FOLDER_ID with personalized order
          folderId: ALL_FOLDER_ID,
          order: GalleryOrder.PERSONALIZED,
        }
      : {
          // SELECTED mode: chosen gallery (or ALL) with newest order
          folderId: finalFolderId,
          order: GalleryOrder.NEWEST,
        };

    onSaveSettings(updatedSettings);
    requestClose?.();
  }, [selectedDisplayType, selectedGallery, onSaveSettings, requestClose]);

  const handleGalleryChange = useCallback(
    (isSelected: boolean, value: string) => {
      // Only allow selection, enforce single-select behavior
      if (isSelected) {
        setSelectedGallery(value);
      }
    },
    []
  );

  // Determine if we should show the upgrade button
  const shouldShowUpgradeButton = useMemo(() => {
    if (
      hasCoreMembership ||
      selectedDisplayType !== GalleryDisplayType.SELECTED ||
      !selectedGallery
    ) {
      return false;
    }
    // Non-Core member selected a non-Featured, non-All folder
    const selectedFolderId = parseInt(selectedGallery, 10);
    return (
      selectedFolderId !== featuredFolderId &&
      selectedFolderId !== ALL_FOLDER_ID
    );
  }, [
    hasCoreMembership,
    selectedDisplayType,
    selectedGallery,
    featuredFolderId,
  ]);

  // Build base gallery options (expensive: hierarchy, sorting, filtering)
  // This memo only recalculates when galleries/membership/translations change
  const baseGalleryOptions = useMemo<Omit<DropdownOption, 'selected'>[]>(() => {
    if (!galleries || galleries.length === 0) {
      return [
        {
          value: ALL_FOLDER_ID.toString(),
          label: t('galleryDisplaySettings.gallery.all'),
        },
      ];
    }

    const { sortedGalleries, depthMap } = buildGalleryHierarchy(galleries);

    // Add "All" as the first option
    const allOption: Omit<DropdownOption, 'selected'> = {
      value: ALL_FOLDER_ID.toString(),
      label: t('galleryDisplaySettings.gallery.all'),
      indentLevel: 0,
    };

    // Transform to dropdown options with indentation, filtering out Scraps and sub-galleries
    const galleryOptionsList = sortedGalleries
      .filter(
        gallery => gallery.folderId !== SCRAPS_FOLDER_ID && !gallery.parentId
      )
      .map(gallery => {
        const isFeatured = gallery.folderId === featuredFolderId;
        const showCoreSymbol = !isFeatured && !hasCoreMembership;
        const galleryValue = gallery.folderId.toString();

        return {
          value: galleryValue,
          label: (
            <span>
              {gallery.name}{' '}
              {showCoreSymbol && (
                <CoreSymbol
                  size={IconSize.SMALL}
                  className={s['inlineCoreSymbol']}
                />
              )}
            </span>
          ),
          indentLevel: depthMap.get(gallery.folderId) || 0,
        };
      });

    return [allOption, ...galleryOptionsList];
  }, [galleries, featuredFolderId, hasCoreMembership, t]);

  // Apply selection state (cheap: just map to add selected property)
  // This memo recalculates quickly on every selection change
  const galleryOptions = useMemo<DropdownOption[]>(
    () =>
      baseGalleryOptions.map(option => ({
        ...option,
        selected: selectedGallery === option.value,
      })),
    [baseGalleryOptions, selectedGallery]
  );

  const footerButtons = useMemo(
    () => [
      <TextButton
        key="cancel"
        onClick={requestClose}
        size="medium"
        variant="main"
      >
        {t('popup.cancel')}
      </TextButton>,
      shouldShowUpgradeButton ? (
        <BuyCoreBrandedButton
          key="upgrade"
          size="medium"
          biData={BiData<BuyCoreButtonBiEvent>({
            evid: 375,
            referral_info: ReferralType.GALLERY_DISPLAY_SETTINGS,
          })}
        >
          {t('common.upgradeToCoreCTA')}
        </BuyCoreBrandedButton>
      ) : (
        <BrandedButton
          key="save"
          onClick={handleSave}
          size="medium"
          variant="brand-green"
        >
          {t('common.save')}
        </BrandedButton>
      ),
    ],
    [requestClose, shouldShowUpgradeButton, handleSave, t]
  );

  return (
    <DSModal
      isOpen
      heading={t('galleryDisplaySettings.title')}
      subheading={!isMobile && t('galleryDisplaySettings.description')}
      showHeaderBorder
      className={s['root']}
      onRequestClose={requestClose}
      withScrollableContentWrapper={false}
      useFixedMobileHeight
      showFooterBorder
      footerRight={footerButtons}
      bodyScrollLockOptions={{
        allowTouchMove: (el: HTMLElement) => {
          return el.closest('#gallery-select-list') !== null;
        },
      }}
    >
      <div className={s['content']}>
        {isLoading && (
          <div className={s['loadingState']}>
            <LoadingIndicator view="llama" />
            <div className={s['loadingText']}>
              {t('galleryDisplaySettings.loadingSettings')}
            </div>
          </div>
        )}
        {!isLoading && isMobile && (
          <div className={s['description']}>
            {t('galleryDisplaySettings.description')}
          </div>
        )}
        {!isLoading && (
          <div className={s['options']}>
            {DISPLAY_TYPE_OPTIONS.map(
              ({ type, titleKey, descriptionKey, descriptionId }) => (
                <div key={type} className={s['option']}>
                  <RadioButton
                    name="displayType"
                    value={type}
                    checked={selectedDisplayType === type}
                    onChange={() => handleDisplayTypeChange(type)}
                    className={s['radio']}
                    aria-describedby={descriptionId}
                    id={`display-type-${type}`}
                  />
                  <div className={s['optionContent']}>
                    <label
                      htmlFor={`display-type-${type}`}
                      className={s['optionTitle']}
                    >
                      {t(titleKey)}
                    </label>
                    <label
                      htmlFor={`display-type-${type}`}
                      id={descriptionId}
                      className={s['optionDescription']}
                    >
                      {t(descriptionKey)}
                    </label>
                    {type === GalleryDisplayType.SELECTED &&
                      selectedDisplayType === type && (
                        <div className={s['galleryDropdown']}>
                          <MultiselectField
                            id="gallery-select"
                            label={t('galleryDisplaySettings.selectGallery')}
                            isLabelHidden
                            items={galleryOptions}
                            onChange={handleGalleryChange}
                            withFloatingList
                            variant="radio"
                            listClassName={s['gallerySelectList']}
                          />
                        </div>
                      )}
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </DSModal>
  );
};

GalleryDisplaySettingsModal.displayName = 'GalleryDisplaySettingsModal';

export default GalleryDisplaySettingsModal;
